// Resolves a thrown error to the copy the user should see on the Admin Management screens.
//
// SHCT-685 established the two halves of this: `admin.api.ts` allowlists codes at the wire boundary,
// so anything the screen does not model collapses to `UNKNOWN` and its raw server text is dropped
// there; this helper then decides, per surviving code, what may be shown.
//
// SHCT-702 moved the decision into a shared resolver, because every other feature had written the
// same rule as `defaultValue: err.message` and was publishing server text for any code without a key.
// This screen is the one place where the server's message is still allowed through — the boundary
// allowlist guarantees only modelled codes reach here, and several of them (the self-status guards,
// the target-status conflicts) are served by the server's own locale-aware copy rather than by a key.

import type { TFunction } from 'i18next';

import { resolveApiErrorMessage } from '@/shared/utils/api-error-message';

import { AdminApiError } from '../types/admin.types';

const KEY_PREFIX = 'adminManagement.errors.';

/**
 * Codes whose server message may be shown when no key exists for them.
 *
 * Safe here, and only here, because `admin.api.ts` already rejected every unmodelled code before this
 * point. Listing them explicitly rather than allowing "anything modelled" keeps a newly added code
 * from inheriting the permission by accident — a new code shows the operation fallback until someone
 * decides otherwise.
 */
const SERVER_MESSAGE_ALLOWED: ReadonlySet<string> = new Set([
  'EMAIL_DUPLICATE',
  'EMAIL_INVALID',
  'PHONE_INVALID',
  'PHONE_DUPLICATE',
  'NAME_INVALID',
  'ROLE_REQUIRED',
  'ROOT_NOT_EDITABLE',
  'ROLE_IN_USE_BY_SELF',
  'SELF_STATUS_CHANGE_FORBIDDEN',
  'SELF_INACTIVATE_FORBIDDEN',
  'SELF_WITHDRAW_FORBIDDEN',
  'INVALID_TARGET_STATUS',
  'TARGET_INVALID',
  'PASSWORD_INVALID',
  'LOCKED_5MIN',
  'NO_CANDIDATES',
]);

export function getAdminErrorMessage(
  err: unknown,
  t: TFunction,
  fallbackKey: string,
): string {
  if (!(err instanceof AdminApiError)) return t(fallbackKey);
  return resolveApiErrorMessage(t, err.code, fallbackKey, err.message, {
    keyPrefix: KEY_PREFIX,
    allowServerMessageFor: SERVER_MESSAGE_ALLOWED,
  });
}
